import React from "react";
import { toast } from "sonner";
import { Copy, FileDown, Check, AlertTriangle, Eye } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { PainBanner, Pill } from "./PersonaUi";

const REASONS = [
  ["Professional money is arriving", "9 institutional funds opened new positions last quarter — about 2.6x the usual number for a technology stock this size. Two are among the best technology stock-pickers we track."],
  ["Their bet is already working", "Since those funds bought, DELL has beaten the technology sector by 4.1%, and every one of the 9 new buyers is in profit."],
  ["Nobody skilled is leaving", "None of the highly skilled holders has sold or trimmed in the last two quarters."],
];
const RISKS = ["Technology positions are concentrated — DELL moves with the sector (high beta).", "Results are reported Nov 2; the setup into the print is favourable, but earnings can move the stock sharply either way."];
const REVISIT = ["Skilled holders begin to exit (2 or more)", "The stock falls well below the price professionals paid", "The short-term trend versus the sector turns negative"];

const Gauge = () => (
  <div className="space-y-1" data-testid="ria-onepager-basis-gauge">
    <div className="relative h-2.5 rounded-full bg-gradient-to-r from-red-500/50 via-zinc-600 to-emerald-500/60">
      <div className="absolute -top-1 w-1 bg-white rounded" style={{ left: "62%", height: 18 }} />
    </div>
    <div className="flex justify-between text-[10px] text-muted-foreground"><span>pros losing</span><span>break-even</span><span>pros winning</span></div>
  </div>
);

export default function RiaOnePager() {
  return (
    <div className="space-y-4" data-testid="persona-ria-onepager">
      <PainBanner testid="persona-ria-onepager-pain" persona="RIA · CLIENT ONE-PAGER"
        pain="A client asks 'why do we own this?' — I need a clear, defensible, compliance-safe answer in 30 seconds."
        answer="plain-English brief, export-ready" />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" data-testid="ria-onepager-copy-btn" onClick={() => toast("Mockup — copy-for-email not wired yet")}><Copy className="w-4 h-4 mr-1" />Copy for email</Button>
        <Button size="sm" data-testid="ria-onepager-export-btn" onClick={() => toast("Mockup — branded PDF export not wired yet")}><FileDown className="w-4 h-4 mr-1" />Export branded PDF</Button>
      </div>
      <Card className="max-w-4xl mx-auto bg-zinc-950 border-zinc-700" data-testid="ria-onepager-sheet">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Holding brief · prepared for client review · Oct 16, 2026</div>
              <div className="text-3xl font-black tracking-tight mt-1">Dell Technologies <span className="font-mono text-muted-foreground text-xl">DELL</span></div>
              <div className="text-sm text-muted-foreground">Held in your Core Growth portfolio · 2.5% of assets</div>
            </div>
            <div className="text-right">
              <Pill tone="good">PROFESSIONAL SUPPORT: STRONG</Pill>
              <div className="text-[11px] text-muted-foreground mt-2">Independent smart-money view by Kensys</div>
            </div>
          </div>
          <div className="text-lg leading-relaxed" data-testid="ria-onepager-summary">
            In short: <b>skilled professional investors have been buying DELL, their purchase is already paying off, and none of them is selling.</b>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {REASONS.map(([t, b], i) => (
              <div key={t} className="rounded-md border border-zinc-800 p-4" data-testid={`ria-onepager-reason-${i}`}>
                <div className="flex items-center gap-2 mb-2"><Check className="w-4 h-4 text-emerald-400" /><span className="font-semibold text-sm">{t}</span></div>
                <p className="text-[13px] text-foreground/80 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Where professionals stand today</div>
              <Gauge />
              <div className="text-[13px] mt-2">Price <b>$109.40</b> vs professionals' estimated purchase price <b>$101.80</b>.</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" />Risks to keep in mind</div>
              <ul className="space-y-1.5 text-[13px] text-foreground/80 list-disc pl-4">{RISKS.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          </div>
          <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-4" data-testid="ria-onepager-revisit">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300 mb-2 flex items-center gap-1.5"><Eye className="w-3 h-3" />What would make us revisit this position</div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[13px]">{REVISIT.map((r) => <li key={r}>• {r}</li>)}</ul>
            <div className="text-[11px] text-muted-foreground mt-2">We monitor these automatically every night.</div>
          </div>
          <div className="text-[10px] text-muted-foreground border-t border-zinc-800 pt-3 leading-relaxed">
            Based on public SEC 13F filings, which are disclosed with a delay of up to 45 days; purchase prices are estimates. This brief describes institutional positioning
            and is not a recommendation to buy or sell. Past patterns do not guarantee future results. Sample data — mockup.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
